/** @jsx jsx */
import { jsx } from '@emotion/react';

import { compose, setDisplayName } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Grid, Typography, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { TrackingProps } from 'bundles/common/components/withSingleTracked';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import {
  MAX_SCORE_ADVANCED,
  MAX_SCORE_BEGINNER,
  MAX_SCORE_CONVERSANT,
  MAX_SCORE_INTERMEDIATE,
  SKILL_LEVEL_SEGMENTS,
  type SkillsetLevel,
  getCurrentLevel,
  getLevelNames,
} from 'bundles/learner-skills/utils';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import LevelSetScoringSystemLabel from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetScoringSystemLabel';
import LevelSetsFeedbackBanner from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetsFeedbackBanner';
import LevelSetsFeedbackDataProvider from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetsFeedbackDataProvider';
import type { PostLevelSetResults } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetsResultDataProvider';
import LevelSetsResultProgressBar from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetsResultProgressBar';
import StarsSVG from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/StarsSVG';

import _t from 'i18n!nls/program-skills-objectives';

const TrackedButton = compose<TrackingProps & ButtonProps, TrackingProps & ButtonProps>(
  setDisplayName('TrackedButton'),
  withSingleTracked({ type: 'BUTTON' })
)(Button);

export { TrackedButton };

function getLevelMessages(currentLevel: SkillsetLevel) {
  const levelNames = getLevelNames();
  const longLevelDescriptions = {
    conversant: _t('You got a new score of {skillScore, number} which is at a conversant skill level.'),
    beginner: _t('You got a new score of {skillScore, number} which is at a beginner skill level.'),
    intermediate: _t('You got a new score of {skillScore, number} which is at an intermediate skill level.'),
    advanced: _t('You got a new score of {skillScore, number} which is at an advanced skill level.'),
  };
  return {
    levelNames,
    longLevelDescriptions,
    levelName: levelNames[currentLevel],
    longLevelDescription: longLevelDescriptions[currentLevel],
  };
}

type Props = {
  skillId: string;
  userId: number;
  skillName: string;
  skillScore: number;
  levelsetResults: PostLevelSetResults;
  onCloseUpdatedModal: () => void;
};

const LevelSetsResultBody = ({
  skillName,
  skillScore,
  levelsetResults,
  onCloseUpdatedModal,
  userId,
  skillId,
}: Props) => {
  const { levelName, levelNames, longLevelDescription } = getLevelMessages(getCurrentLevel(skillScore));

  const leftAlignTextCss = {
    textAlign: 'left',
    width: '100%',
  } as const;

  const totalQuestions =
    levelsetResults.correctQuestions + levelsetResults.wrongQuestions + levelsetResults.skippedQuestions;

  return (
    <div
      css={{
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'var(--cds-spacing-600)',
      }}
    >
      <Typography component="h3" variant="h1semibold">
        <FormattedMessage message={_t('Your {skillName} Score: ')} skillName={skillName} />
      </Typography>
      <Typography
        component="span"
        variant="d2semibold"
        color="highlightBlue"
      >{`${levelName} (${skillScore})`}</Typography>
      <LevelSetsResultProgressBar
        skillName={skillName}
        skillScore={skillScore}
        maxScore={500}
        segments={SKILL_LEVEL_SEGMENTS}
      />
      <LevelSetScoringSystemLabel
        levels={[
          { levelName: levelNames.conversant, minValue: 0, maxValue: MAX_SCORE_CONVERSANT },
          { levelName: levelNames.beginner, minValue: MAX_SCORE_CONVERSANT + 1, maxValue: MAX_SCORE_BEGINNER },
          { levelName: levelNames.intermediate, minValue: MAX_SCORE_BEGINNER + 1, maxValue: MAX_SCORE_INTERMEDIATE },
          { levelName: levelNames.advanced, minValue: MAX_SCORE_INTERMEDIATE + 1, maxValue: MAX_SCORE_ADVANCED },
        ]}
      />
      {levelsetResults.learnersScorePercentile <= 30 && (
        <div css={{ display: 'flex', justifyContent: 'start', alignItems: 'center', width: '100%' }}>
          <Typography
            component="span"
            css={{ paddingRight: 'var(--cds-spacing-100)' }}
            color="highlightBlue"
            variant="h1semibold"
          >
            {_t('Your skill score is in the top 30%!')}
          </Typography>
          <StarsSVG />
        </div>
      )}
      <ul css={{ width: '100%', paddingLeft: 'var(--cds-spacing-200)' }}>
        <Typography2
          variant="subtitleMedium"
          color="highlightBlue"
          component="li"
          css={{
            ...leftAlignTextCss,
            marginTop: 'var(--cds-spacing-200)',
          }}
        >
          {levelsetResults.learnersScorePercentile <= 30 // Top 30 percentile
            ? _t(
                'You’re doing great! Your score for this skill is in the top 30th percentile of all learners on Coursera. '
              )
            : _t(
                'Your score for this skill isn’t in the top 30th percentile of all learners on Coursera. You’re doing ok, but there is room for improvement.'
              )}
        </Typography2>

        <Typography2 variant="subtitleMedium" color="highlightBlue" component="li" css={leftAlignTextCss}>
          <FormattedMessage message={longLevelDescription} skillScore={skillScore} />
        </Typography2>

        <Typography2
          variant="subtitleMedium"
          color="highlightBlue"
          css={{
            ...leftAlignTextCss,
            marginBottom: 'var(--cds-spacing-400)',
          }}
          component="li"
        >
          <FormattedMessage
            message={_t(
              'You got {percentage, number}% on your assessment ({correctQuestion, number}/{totalQuestions, number} questions).'
            )}
            percentage={Math.floor((100 * levelsetResults.correctQuestions) / totalQuestions)}
            correctQuestion={levelsetResults.correctQuestions}
            totalQuestions={totalQuestions}
          />
        </Typography2>
      </ul>
      <LevelSetsFeedbackDataProvider userId={userId} skillId={skillId} attemptNumber={levelsetResults.attemptNumber}>
        {(mutate) => <LevelSetsFeedbackBanner onFeedbackGiven={mutate} />}
      </LevelSetsFeedbackDataProvider>
      <Typography component="p" css={leftAlignTextCss} variant="h2semibold" color="body">
        <FormattedMessage
          message={_t('Next, improve your {skillName} skills with our recommended courses')}
          skillName={skillName}
        />
      </Typography>
      <Typography component="p" css={leftAlignTextCss} variant="h2" color="supportText">
        {_t(
          'We have targeted recommendations for you based on your results and skill gaps. View all recommendations to continue your learning journey.'
        )}
      </Typography>
      <Grid container>
        <Grid item xs={12} sm="auto">
          <TrackedButton
            {...Button.defaultProps}
            trackingName="view_all_recommendations"
            trackingData={{
              userId,
              skillId,
            }}
            fullWidth={true}
            onClick={onCloseUpdatedModal}
            css={{
              marginRight: 'auto',
              marginTop: 'var(--cds-spacing-400)',
            }}
            iconPosition="after"
            icon={<ArrowNextIcon size="small" />}
          >
            {_t('View all recommendations')}
          </TrackedButton>
        </Grid>
      </Grid>
    </div>
  );
};

export default LevelSetsResultBody;
